import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const EMPTY = new Set();

/**
 * Tracks which task rows are ticked.
 *
 * Keyed by task id rather than row index because virtualised rows unmount as
 * they scroll out of view. `scope` is whatever identifies the current sort and
 * search; when it changes the selection is dropped, since the rows a user
 * picked are no longer the rows on screen.
 */
const useTaskRowSelection = ({ rows, scope }) => {
	const [selectedIds, setSelectedIds] = useState(EMPTY);
	const selectedIdsRef = useRef(selectedIds);

	const apply = useCallback((next) => {
		selectedIdsRef.current = next;
		setSelectedIds(next);
	}, []);

	useEffect(() => {
		if (selectedIdsRef.current.size === 0) return;
		apply(EMPTY);
		// Only the scope matters here: new rows arriving on the same sort and
		// search must not disturb an in-progress selection.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [scope]);

	const toggleRow = useCallback(
		(taskId, shouldSelect) => {
			const next = new Set(selectedIdsRef.current);
			if (shouldSelect) next.add(taskId);
			else next.delete(taskId);
			apply(next);
		},
		[apply]
	);

	const toggleAllLoaded = useCallback(
		(shouldSelect) => {
			if (!shouldSelect) return apply(EMPTY);
			return apply(new Set(rows.map((row) => row.original.id)));
		},
		[apply, rows]
	);

	const clear = useCallback(() => apply(EMPTY), [apply]);

	const isSelected = useCallback(
		(taskId) => selectedIds.has(taskId),
		[selectedIds]
	);

	const selectedRows = useMemo(
		() => rows.filter((row) => selectedIds.has(row.original.id)).map((r) => r.original),
		[rows, selectedIds]
	);

	const headerState = useMemo(() => {
		if (selectedIds.size === 0) return "none";
		return selectedRows.length === rows.length && rows.length > 0
			? "all"
			: "some";
	}, [rows.length, selectedIds.size, selectedRows.length]);

	return {
		selectedIds,
		selectedRows,
		// Counted from the rows that still exist, not the raw id set: a task
		// deleted from under the selection must not keep inflating the count.
		selectedCount: selectedRows.length,
		headerState,
		isSelected,
		toggleRow,
		toggleAllLoaded,
		clear,
	};
};

export default useTaskRowSelection;
